import { AttributesMixin, define, Model, type } from "@type-r/models";
import { FilterType, GetFilteredValidationRulesRequest } from "../validation-rules/models/get-filtered-validation-rules-request";
import { CompanyItem } from "./company";
import { getAuthorizationBearer, getPlatformCode } from "./endpoint";

export interface ValidationRuleStatus extends AttributesMixin<typeof ValidationRuleStatus> { }

@define
export class ValidationRuleStatus extends Model {
  static attributes = {
    isActive: Boolean
  }
}

export interface ValidationRuleItem extends AttributesMixin<typeof ValidationRuleItem> { }
@define
export class ValidationRuleItem extends Model {
  static attributes = {
    id: String,
    name: String,
    isDefault: Boolean,
    isActive: Boolean,
    isStandard: Boolean,
    isUserDefined: Boolean,
    assetTypeId: Number,
    createdBy: String,
    createdOn: Date,
    updatedOn: type(Date).null,
    companies: [CompanyItem]
  };

  async delete(): Promise<void> {
    const url = `api/v1/ingest/validation-rules/${this.id}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: new Headers({
        "Authorization": getAuthorizationBearer(),
        'Content-Type': 'application/json',
        'X-Platform-Code': getPlatformCode()
      })
    });
    if (response.status < 200 || response.status >= 300) {
      let error = response.statusText || `HTTP Error ${response.status}`;
      await response.json()
        .then(e => error = e.error);
      throw new Error(error);
    }
    return await response.json();
  }

  async switchStatus(): Promise<ValidationRuleStatus> {
    const url = `api/v1/ingest/validation-rules/${this.id}/switch`;

    const payload = {
      id: this.id,
      isActive: !this.isActive
    };

    const response = await fetch(url, {
      method: 'PUT',
      headers: new Headers({
        "Authorization": getAuthorizationBearer(),
        'Content-Type': 'application/json',
        'X-Platform-Code': getPlatformCode()
      }),
      body: JSON.stringify(payload)
    });

    if (response.status < 200 || response.status >= 300) {
      let error = response.statusText || `HTTP Error ${response.status}`;
      await response.json()
        .then(e => error = e.error)
        .catch(e => console.error('Cannot parse response data'));
      throw new Error(error);
    }

    const json = await response.json();
    this.set({
      isActive: json.isActive
    });
    return this;
  }
}

export interface ValidationRules extends AttributesMixin<typeof ValidationRules> { }
@define
export class ValidationRules extends Model {
  static attributes = {
    items: [ValidationRuleItem]
  }
}

export async function getFiltered(request?: GetFilteredValidationRulesRequest): Promise<ValidationRules> {
  let url: string = 'api/v1/ingest/validation-rules';

  if (request) {
    const searchParams = new URLSearchParams();
    if (request.companyIds && +request.filterType === FilterType.company) {
      request.companyIds.map((item: any) => {
        searchParams.append("companyIds", `${item.id}`);
      });
    }
    if (request.assetTypeId && +request.filterType === FilterType.assetType) {
      searchParams.append("assetTypeId", `${request.assetTypeId}`);
    }
    if (request.quickSearch && +request.filterType === FilterType.all) {
      searchParams.append("quickSearch", `${request.quickSearch}`);
    }
    searchParams.append("filterType", `${request.filterType}`);
    searchParams.append("sortBy", `${request.sortBy}`);
    searchParams.append("asc", `${request.asc}`);

    var seracsearchParamsURl = searchParams.toString()
    if (seracsearchParamsURl) {
      url += `?${seracsearchParamsURl}`
    }
  }

  return (await fetch(url, {
    method: 'GET',
    headers: new Headers({
      "Authorization": getAuthorizationBearer(),
      'X-Platform-Code': getPlatformCode()
    }),
  })).json();
}

export interface ValidationSetItem extends AttributesMixin<typeof ValidationSetItem> { }
@define
export class ValidationSetItem extends Model {
    static attributes = {
        name: String
    }
}

export interface ValidationSets extends AttributesMixin<typeof ValidationSets> { }
@define
export class ValidationSets extends Model {
    static attributes = {
        items: [ValidationSetItem]
    }
}

export async function getValidationSets(quicksearch?: string, assetTypeId?: number): Promise<ValidationSets> {
    let parameters = [
        quicksearch ? `quicksearch=${quicksearch}` : '',
        assetTypeId ? `assetTypeId=${assetTypeId}` : ''
    ];

    let urlParameters = parameters.some(s => s !== '') ? `?${parameters.filter(s => s !== '').join('&')}` : '';

    let url: string = `api/v1/ingest/validation-rules/filter${urlParameters}`;
    return (await fetch(url, {
        method: 'GET',
        headers: new Headers({
            "Authorization": getAuthorizationBearer()
        }),
    })).json();
}
